import { gql } from '@apollo/client'

// Template-editor documents that are newer than the ones in cadence.query.js. Each is a
// separate operation on purpose: if the API has not shipped a field yet, only the feature
// that needs it degrades, instead of the template inventory failing to load.

const TEMPLATE_FIELDS = 'id key name subject body status version usageCount updatedAt'

// ── Template channels (SPT-2316) ─────────────────────────────────────────────
// Everything that depends on the gem's channel fields (maestro-api-only !320) lives in this block, so a
// rename there is a one-place change here. Template `channel: String!` is email | sms | push |
// in_app_message; `saveCadenceEmailTemplate(channel: String)` sets it on create only (default email; an
// update that changes it is refused); `subject` is optional there and stored as "" for sms / in_app_message.
// `cadenceEmailTemplates(channel: String)` filters (omitted = all) and `previewCadenceEmailTemplate(channel:
// String)` renders sms / push / in-app bodies as plain text. `subjectB` (A/B subject) is not used by this UI.
// Each document is its own operation: an API without `channel` rejects only these, and every caller falls
// back to the channel-less documents above/in cadence.query.js (where every template is an email template).
export const TEMPLATE_CHANNEL_FIELDS = 'channel'

// Template library and pickers: every channel, each template carrying its channel.
export const CADENCE_TEMPLATE_CHANNELS_QUERY = gql`
	query cadenceTemplateChannels {
		cadenceEmailTemplates { ${TEMPLATE_FIELDS} ${TEMPLATE_CHANNEL_FIELDS} }
	}
`

// One template by id (the editor's direct lookup, e.g. an archived template).
export const CADENCE_CHANNEL_TEMPLATE_QUERY = gql`
	query cadenceChannelTemplate($id: ID!) {
		cadenceEmailTemplate(id: $id) { ${TEMPLATE_FIELDS} ${TEMPLATE_CHANNEL_FIELDS} }
	}
`

// SMS / push / in-app templates. `subject` carries the push title; it is omitted for SMS and in-app.
// `$channel` is sent only when creating; omit it on update (the channel cannot change).
export const SAVE_CADENCE_CHANNEL_TEMPLATE_MUTATION = gql`
	mutation saveCadenceChannelTemplate($id: ID, $key: String!, $name: String!, $subject: String, $body: String!, $channel: String) {
		saveCadenceEmailTemplate(id: $id, key: $key, name: $name, subject: $subject, body: $body, channel: $channel) {
			${TEMPLATE_FIELDS} ${TEMPLATE_CHANNEL_FIELDS}
		}
	}
`

// Server render of an SMS / push / in-app draft with the sample candidate (plain text). `subject` is the push
// title ("" for SMS and in-app).
export const PREVIEW_CADENCE_CHANNEL_TEMPLATE_QUERY = gql`
	query previewCadenceChannelTemplate($id: ID, $subject: String, $body: String, $channel: String) {
		previewCadenceEmailTemplate(id: $id, subject: $subject, body: $body, channel: $channel) {
			subject
			body
		}
	}
`

export const CADENCE_MERGE_VARIABLES_QUERY = gql`
	query cadenceMergeVariables {
		cadenceMergeVariables {
			token
			label
			group
			field
			sample
			available
		}
	}
`

export const CADENCE_EMAIL_TEMPLATE_USAGE_QUERY = gql`
	query cadenceEmailTemplateUsage($id: ID!) {
		cadenceEmailTemplate(id: $id) {
			id
			usageCount
			usedByCadences {
				id
				name
				status
			}
		}
	}
`

export const DUPLICATE_CADENCE_EMAIL_TEMPLATE_MUTATION = gql`
	mutation duplicateCadenceEmailTemplate($id: ID!, $name: String, $key: String) {
		duplicateCadenceEmailTemplate(id: $id, name: $name, key: $key) { ${TEMPLATE_FIELDS} }
	}
`

export const SEND_CADENCE_TEST_EMAIL_MUTATION = gql`
	mutation sendCadenceTestEmail($templateId: ID!, $to: String!, $workerId: ID, $subject: String, $body: String) {
		sendCadenceTestEmail(templateId: $templateId, to: $to, workerId: $workerId, subject: $subject, body: $body) {
			success
			resolvedTags
		}
	}
`

// Sender identity lives on the template. These documents are only used once
// CADENCE_SENDER_SETTINGS_QUERY succeeds, so an API without them keeps the older
// save/test-send operations and the read-only sender notice.
const TEMPLATE_SENDER_FIELDS = 'senderName senderEmail replyTo senderVerified effectiveSenderName effectiveSenderEmail'

export const CADENCE_SENDER_SETTINGS_QUERY = gql`
	query cadenceSenderSettings {
		cadenceSenderDomains {
			domain
			verified
		}
		cadenceDefaultSender {
			name
			email
		}
	}
`

export const CADENCE_EMAIL_TEMPLATE_SENDER_QUERY = gql`
	query cadenceEmailTemplateSender($id: ID!) {
		cadenceEmailTemplate(id: $id) { id ${TEMPLATE_SENDER_FIELDS} }
	}
`

export const SAVE_CADENCE_EMAIL_TEMPLATE_WITH_SENDER_MUTATION = gql`
	mutation saveCadenceEmailTemplateWithSender(
		$id: ID
		$key: String!
		$name: String!
		$subject: String!
		$body: String!
		$senderName: String
		$senderEmail: String
		$replyTo: String
	) {
		saveCadenceEmailTemplate(
			id: $id
			key: $key
			name: $name
			subject: $subject
			body: $body
			senderName: $senderName
			senderEmail: $senderEmail
			replyTo: $replyTo
		) { ${TEMPLATE_FIELDS} ${TEMPLATE_SENDER_FIELDS} }
	}
`

export const SEND_CADENCE_TEST_EMAIL_WITH_SENDER_MUTATION = gql`
	mutation sendCadenceTestEmailWithSender(
		$templateId: ID!
		$to: String!
		$workerId: ID
		$subject: String
		$body: String
		$senderName: String
		$senderEmail: String
		$replyTo: String
	) {
		sendCadenceTestEmail(
			templateId: $templateId
			to: $to
			workerId: $workerId
			subject: $subject
			body: $body
			senderName: $senderName
			senderEmail: $senderEmail
			replyTo: $replyTo
		) {
			success
			resolvedTags
		}
	}
`

// Smart communication S4: "Send as the candidate's recruiter" (email templates only). A separate set of documents,
// like the sender ones above: they are used only once CADENCE_SEND_AS_RECRUITER_SUPPORT_QUERY succeeds, so an API
// without the field keeps the sender documents unchanged. `sendAsRecruiter` omitted or null on save keeps the stored value.
export const TEMPLATE_SEND_AS_RECRUITER_FIELDS = 'sendAsRecruiter'

export const CADENCE_SEND_AS_RECRUITER_SUPPORT_QUERY = gql`
	query cadenceSendAsRecruiterSupport {
		cadenceEmailTemplates { id ${TEMPLATE_SEND_AS_RECRUITER_FIELDS} }
	}
`

export const CADENCE_EMAIL_TEMPLATE_RECRUITER_SENDER_QUERY = gql`
	query cadenceEmailTemplateRecruiterSender($id: ID!) {
		cadenceEmailTemplate(id: $id) { id ${TEMPLATE_SENDER_FIELDS} ${TEMPLATE_SEND_AS_RECRUITER_FIELDS} }
	}
`

export const SAVE_CADENCE_EMAIL_TEMPLATE_WITH_RECRUITER_SENDER_MUTATION = gql`
	mutation saveCadenceEmailTemplateWithRecruiterSender(
		$id: ID
		$key: String!
		$name: String!
		$subject: String!
		$body: String!
		$senderName: String
		$senderEmail: String
		$replyTo: String
		$sendAsRecruiter: Boolean
	) {
		saveCadenceEmailTemplate(
			id: $id
			key: $key
			name: $name
			subject: $subject
			body: $body
			senderName: $senderName
			senderEmail: $senderEmail
			replyTo: $replyTo
			sendAsRecruiter: $sendAsRecruiter
		) { ${TEMPLATE_FIELDS} ${TEMPLATE_SENDER_FIELDS} ${TEMPLATE_SEND_AS_RECRUITER_FIELDS} }
	}
`

// ── AI assist (smart comms S5) ───────────────────────────────────────────────
// Own documents, so an API without AI assist only hides the panel.
export const CADENCE_AI_ASSIST_QUERY = gql`
	query cadenceAiAssist {
		cadenceAiAssist {
			enabled
			limit
			used
			remaining
			resetsOn
		}
	}
`

export const CADENCE_TEMPLATE_LINT_QUERY = gql`
	query cadenceTemplateLint($channel: String!, $subject: String, $body: String!) {
		cadenceTemplateLint(channel: $channel, subject: $subject, body: $body) {
			code
			severity
			message
		}
	}
`

export const DRAFT_CADENCE_TEMPLATE_WITH_AI_MUTATION = gql`
	mutation draftCadenceTemplateWithAi($channel: String!, $brief: String, $subject: String, $body: String, $subjectsOnly: Boolean) {
		draftCadenceTemplateWithAi(channel: $channel, brief: $brief, subject: $subject, body: $body, subjectsOnly: $subjectsOnly) {
			subject
			body
			subjects
			remaining
		}
	}
`

// Write with AI (a conversation in the template editor): one request. The browser keeps the conversation and sends
// the version being changed (no subject or body for a blank template) and the requests that produced it, oldest first.
export const REVISE_CADENCE_TEMPLATE_WITH_AI_MUTATION = gql`
	mutation reviseCadenceTemplateWithAi($channel: String!, $request: String!, $earlierRequests: [String!], $subject: String, $body: String) {
		reviseCadenceTemplateWithAi(channel: $channel, request: $request, earlierRequests: $earlierRequests, subject: $subject, body: $body) {
			reply
			changed
			subject
			body
			remaining
		}
	}
`

// Write with AI, deploy-skew probe: the field is @skip(if: true), so a schema that has it validates the document and
// runs nothing (no AI call, no request used). A schema without it, or a host that cannot serve it (the field is hidden
// there), rejects the document at validation ("doesn't exist", undefinedField).
export const CADENCE_TEMPLATE_AI_REVISE_PROBE_MUTATION = gql`
	mutation cadenceTemplateAiReviseProbe {
		reviseCadenceTemplateWithAi(channel: "email", request: "x") @skip(if: true) {
			reply
		}
	}
`

// SPT-2323: the company's email brand colour, set once and read by every candidate email. Any recruiter may read it;
// canEdit is true only for a Maestro admin signed in as themself.
export const TENANT_EMAIL_BRAND_QUERY = gql`
	query tenantEmailBrand {
		tenantEmailBrand {
			brand
			brandColor
			buttonColor
			buttonTextColor
			buttonFontSize
			linkColor
			exactColors
			canEdit
		}
	}
`

export const UPDATE_TENANT_EMAIL_BRAND_MUTATION = gql`
	mutation updateTenantEmailBrand($brand: String!, $exactColors: Boolean) {
		updateTenantEmailBrand(brand: $brand, exactColors: $exactColors) {
			brand
			brandColor
			buttonColor
			buttonTextColor
			buttonFontSize
			linkColor
			exactColors
			canEdit
		}
	}
`
